import cookie from './cookie.js';
import ImsAuth from './ims_auth.js';
import ImsEvents from './ims_events.js';
import { getPaywallConfig } from './link_paywall_class.js';
import { addPageAction } from './new_relic.js';
import { createPostLoginAction, getPostLoginActionParamsForUrl } from './post_login_actions/post_login_creator.js';
import { openSUSILightDialog, showSignInUnavailableError } from './susi_light_util.js';
import UrlService from './url_service.js';

const SUSI_LIGHT_SELECTOR = 'af-susi-light';
const SENTRY_LIGHT_SELECTOR = 'susi-sentry-light';

// Names of the require-login experiment cookies, kept in sync with the server
// (RequireLoginExperiment). Both are non-HttpOnly, so they're readable/writable
export const REQUIRE_LOGIN_NUM_CLICKS_COOKIE = 'require_login_experiment_num_clicks';
export const REQUIRE_LOGIN_STATE_COOKIE = 'require_login_experiment_state';

/**
 * If the user is already logged in, this method will resolve a promise
 * immediately without redirecting or creating a post login action.
 *
 * If the user is not already logged in, this method will create a post login
 * action on the server side and then redirect the user to the Adobe login
 * page with the post login action id in the url.
 *
 * @param {String} actionName The name of the action that should be executed after login
 * @param {Object} data An object representing the extra data needed to complete the action
 *
 * @return {Promise}
 */
export function handleAuthenticationWithPostLoginAction(actionName, data) {
  return new Promise((resolve, reject) => {
    ImsEvents.addOnReadyHandler(function() {
      if (ImsAuth.isSignedInUser()) {
        resolve();
      } else {
        // component only shows up if enable_susi_light flipper flag is enabled
        const susiLight = document.querySelector(SUSI_LIGHT_SELECTOR);
        if (susiLight) {
          createPostLoginAction(actionName, data)
          .then(postLoginActionId => {
            updateSusiLightRedirectUri(postLoginActionId);
            openSUSILightDialog(getPostLoginUrl(postLoginActionId));
          })
          .catch(reject);
        } else {
          createPostLoginAction(actionName, data)
          .then(redirectToLoginPage)
          .catch(reject);
        }
      }
    });
  });
}

async function handleRequireLoginLinkClick(event) {
  event.preventDefault();
  const path = this.href;
  const susiLight = document.querySelector(SUSI_LIGHT_SELECTOR);
  const sentryLight = document.querySelector(SENTRY_LIGHT_SELECTOR);
  if (!susiLight || !sentryLight) {
    // If for some reason the susi light or sentry light element isn't loaded, don't trigger require login flow
    // postlogin actions have their own separate handling that won't be impacted by link clicking
    window.location = path;
  }

  addPageAction('require_login.paywall_intercept', { href: path });

  const postLoginActionId = await createPostLoginAction('redirect');
  updateSusiLightRedirectUri(postLoginActionId, this.href);
  openSUSILightDialog(this.href);
}

function attachLinksWithPostLoginAction() {
  document.addEventListener('click', handleLinkClick);
  document.addEventListener('keydown', handleLinkClick);
}

export function handleLinkClick(event) {
  if (event.type === 'keydown' && event.key !== 'Enter') {
    return;
  }

  const paywallConfig = getPaywallConfig();
  if (!paywallConfig) {
    return;
  }

  const target = event.composedPath().find(
    el => el.matches && el.matches(paywallConfig.interactiveSelector || '')
  );
  if (!target) {
    return;
  }

  if (target.getAttribute('href')?.startsWith('#')) {
    return;
  }

  if (target.href && target.href.includes(window.location.origin)) {
    handleRequireLoginLinkClick.call(target, event);
  }
}

/**
 * Returns true when we should show the login prompt. All conditions must hold:
 * the user is signed out,
 * they are past the click limit,
 * the SUSI-light element is on the page,
 * and the path is not exempt.
 *
 * @return {Boolean}
 */
export function shouldAttachRequireLoginInterception() {
  if (ImsAuth.isSignedInUser() || !shouldRequireLogin() || !document.querySelector(SUSI_LIGHT_SELECTOR)) {
    return false;
  }

  return !((getPaywallConfig()?.exemptedPaywallClassPaths || []).some(path => window.location.pathname.startsWith(path)));
}

/**
 * Initialize the links with post login action by attaching click event listeners
 * to all anchor elements on the page. This will ensure that any links that require
 * authentication will redirect to the Adobe login page with the appropriate post login action.
 */
export function initLinksWithPostLoginAction() {
  ImsEvents.addOnReadyHandler(() => {
    if (!shouldAttachRequireLoginInterception()) {
      return;
    }

    if (window.alloy_all) {
      window.alloy_all.data._adobe_corpnew.digitalData.page.pageInfo.globalRequireLogin = true;
    }

    attachLinksWithPostLoginAction();
  });
}

/**
 * Re-evaluate require-login state and, if the paywall should now intercept link
 * clicks, attach the delegated click handler. Safe to call repeatedly — the
 * document listener uses a stable function reference, so addEventListener
 * dedupes. Used after client-side content updates (e.g. a filter-sidebar
 * selection whose results fetch crosses the click threshold).
 *
 * Unlike initLinksWithPostLoginAction, this does not set the
 * alloy_all.globalRequireLogin data-layer flag — that is a page-load concern.
 */
export function attachRequireLoginLinkInterception() {
  if (!shouldAttachRequireLoginInterception()) {
    return;
  }

  attachLinksWithPostLoginAction();
}

/**
 * Build the SUSI-light post-login redirect URI for a target URL (path + query),
 * merging the post-login action params into the target's query string. Unlike
 * getPostLoginUrl, this honors the target's own query rather than the current
 * page's params — used when the visible URL has been reverted but we still want
 * to land on the attempted state after login.
 *
 * @param {String} postLoginActionId
 * @param {String} targetUrl path + optional query, e.g. '/fonts/tags/serif?page=2'
 * @return {String}
 */
function buildRequireLoginRedirectUri(postLoginActionId, targetUrl) {
  const url = new URL(targetUrl, window.location.origin);
  const postLoginParams = getPostLoginActionParamsForUrl(postLoginActionId);
  Object.entries(postLoginParams).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  return url.origin + '/redirect/susi?back_to=' +
    encodeURIComponent(url.pathname + '?' + url.searchParams.toString());
}

/**
 * Prompt sign-in for a paywall-intercepted browse interaction (filter/language/pagination
 * change at the click cap). After sign-in the visitor lands on targetUrl (the attempted
 * state). The interaction stays blocked either way; when the SUSI-light dialog can't open
 * (e.g. its external script failed to load, or creating the post-login action failed), an
 * error notification explains why.
 *
 * There's intentionally no full-page sign-in fallback: Product decided (early 2026) that a
 * full-page redirect mid-browse is too confusing. For a sustained SUSI Light outage, turn
 * off the require_login_experiment flag instead.
 *
 * @param {String} targetUrl
 * @return {Promise<void>}
 */
export async function openRequireLoginDialogForUrl(targetUrl) {
  addPageAction('require_login.paywall_intercept', { href: targetUrl });

  const susiLight = document.querySelector(SUSI_LIGHT_SELECTOR);
  // The sentry element is only created once SUSI Light's external script loads.
  if (!susiLight || !document.querySelector(SENTRY_LIGHT_SELECTOR)) {
    reportSignInUnavailable(targetUrl);
    return;
  }

  try {
    const postLoginActionId = await createPostLoginAction('redirect');
    const redirectUri = buildRequireLoginRedirectUri(postLoginActionId, targetUrl);
    susiLight.updateRedirectUri(redirectUri);
    openSUSILightDialog(redirectUri);
  } catch (error) {
    reportSignInUnavailable(targetUrl);
  }
}

/**
 * Tell the visitor sign-in couldn't load, so a blocked interaction isn't silent.
 *
 * @param {String} targetUrl
 */
function reportSignInUnavailable(targetUrl) {
  addPageAction('require_login.paywall_intercept_error', { href: targetUrl });
  showSignInUnavailableError();
}

/**
 * Redirect the page to the Adobe login page, and include an encoded version
 * of the original url + the postLoginActionId as the `back_to` param.
 *
 * @param {String} postLoginActionId
 */
function redirectToLoginPage(postLoginActionId, path = '') {
  const searchParams = new URLSearchParams(UrlService.getSearchParams());
  const postLoginParams = getPostLoginActionParamsForUrl(postLoginActionId);

  Object.entries(postLoginParams).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  path ||= window.location.pathname;
  window.location = '/login/sso?back_to=' +
    encodeURIComponent(path + '?' + searchParams.toString());
}

/**
 * Get the full redirect URL for the post login action.
 * @param {*} postLoginActionId
 * @returns {String} The full redirect URL for the post login action
 */
function getPostLoginUrl(postLoginActionId, path = '') {
  const currentURL = new URL(window.location.href);
  const origin = currentURL.origin;
  const searchParams = new URLSearchParams(UrlService.getSearchParams());
  const postLoginParams = getPostLoginActionParamsForUrl(postLoginActionId);

  Object.entries(postLoginParams).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  path ||= window.location.pathname;
  return origin + '/redirect/susi?back_to=' + encodeURIComponent(path + '?' + searchParams.toString());
}

export function shouldRequireLogin() {
  return !!(cookie(REQUIRE_LOGIN_STATE_COOKIE) &&
    !(cookie(REQUIRE_LOGIN_STATE_COOKIE) || '').includes('logged_out') &&
    (parseInt(cookie(REQUIRE_LOGIN_NUM_CLICKS_COOKIE), 10) || 0) >= (getPaywallConfig()?.maxNumClicksThreshold || 2));
}

/**
 * SUSI Light needs the full redirect URL with the post login action, rather than just the pathname.
 * @param {*} postLoginActionId
 * @param {*} susiLight
 */
function updateSusiLightRedirectUri(postLoginActionId, path = '') {
  const susiLight = document.querySelector(SUSI_LIGHT_SELECTOR);
  const redirectURL = getPostLoginUrl(postLoginActionId, path);
  susiLight.updateRedirectUri(redirectURL);
}
